<template>
	<!-- Sub-Navigation Filters: Mensual / Trimestral / YTD / Anual. Con
		`rangeMode`, el AnualCalendar vive inline a la derecha del grupo — es
		independiente de los períodos (no participa del `is-active`), pero
		comparte fila para que se lea como parte del cluster de "controles del
		período analizado". -->
	<div class="period_filters_row">
		<div class="sub_nav_filters">
			<button
				v-for="period in filterOptions"
				:key="period.id"
				type="button"
				class="filter_btn"
				:class="{ 'is-active': filterPeriod === period.id }"
				@click="$emit('update-filter-period', period.id)">
				{{ period.label }}
			</button>
		</div>
		<AnualCalendar v-if="rangeMode && ['month', 'quarter'].includes(filterPeriod)"
			class="range_inline_calendar"
			mode="range"
			:range-value="dateRange"
			range-trigger-label="Rango"
			:disable-newer-dates="true"
			:disabled-from="getNow"
			:min-date="dataFirstMonth"
			:align-right="false"
			@range-change="(range) => $emit('update-date-range', range)" />
		<!-- YTD: las columnas son años recortados a un mes de corte común.
			El selector combinado (modo "ytd") elige en un solo dropdown el
			span de AÑOS (paso 1) y luego el MES de corte (paso 2), aplicado
			por igual a cada año. Emite `ytd-change` { from, to, endMonth };
			lo desdoblamos en update-year-range (el span) + update-end-month
			(año `to` + mes de corte). Default = el rango de Anual + el mes
			de `endMonth`. -->
		<AnualCalendar v-else-if="rangeMode && filterPeriod === 'ytd'"
			class="range_inline_calendar"
			mode="ytd"
			range-trigger-label="YTD"
			:range-value="yearRange"
			:value="endMonth"
			:disable-newer-dates="true"
			:disabled-from="getNow"
			:min-date="dataFirstMonth"
			:align-right="false"
			@ytd-change="onYtdChange" />
		<!-- Anual: las columnas son años calendario. En vez del default
			fijo (últimos N años) el usuario elige un span de años con la
			misma mecánica de rango — grilla de años en lugar de meses,
			mismo chrome del trigger ("Años  2021 – 2025"). -->
		<AnualCalendar v-else-if="rangeMode && filterPeriod === 'year'"
			class="range_inline_calendar"
			mode="range"
			granularity="year"
			range-trigger-label="Años"
			:range-value="yearRange"
			:disable-newer-dates="true"
			:disabled-from="getNow"
			:min-date="dataFirstMonth"
			:align-right="false"
			@range-change="(range) => $emit('update-year-range', range)" />
	</div>
</template>

<script>
import AnualCalendar from "@/components/classification/AnualCalendar";

// Controles del período analizado de un reporte: los botones de granularidad
// y, en modo rango, el selector de rango que corresponde a cada una. Lo usan
// el header de los tabs (`TabHeader`) y el cuerpo del drawer de Rentabilidad,
// que comparte los filtros con su tab (plan de filtros, 2026-10-05).
// Presentacional: recibe el estado por props y emite los mismos eventos que
// los tabs ya cablean.
export default {
	name: "ReportPeriodFilters",
	components: { AnualCalendar },
	props: {
		// Valor activo del set de períodos (Mensual/Trimestral/YTD/Anual).
		filterPeriod: { type: String, required: true },
		filterOptions: { type: Array, required: true },
		endMonth: { type: Date, required: true },
		// Corte "ahora" del AnualCalendar (los tabs lo exponen como Date).
		getNow: { type: Date, required: true },
		// Con rango, cada granularidad trae su selector inline (rango de
		// meses, YTD o años). Sin rango sólo van los botones: el calendario
		// de mes único lo ubica el padre.
		rangeMode: { type: Boolean, default: false },
		dateRange: { type: Object, default: null },
		// Rango de años activo para el picker de Anual/YTD: `{ from, to }`.
		yearRange: { type: Object, default: null },
	},
	computed: {
		// Piso de los calendarios: primer mes con datos de la empresa
		// (`reportsFilters.dataFirstMonth`). Acceso defensivo: el componente
		// es presentacional y sus specs lo montan sin store; sin módulo el piso
		// queda null (= sin piso).
		dataFirstMonth() {
			const reportsFilters = this.$store && this.$store.state.reportsFilters;
			return reportsFilters ? reportsFilters.dataFirstMonth : null;
		},
	},
	methods: {
		// El selector combinado de YTD emite un único payload con el span de
		// años + el mes de corte (endMonth = año `to` + mes). Lo desdoblamos en
		// los dos eventos que los tabs ya cablean (update-year-range alimenta
		// `customYearRange`; update-end-month, el corte).
		onYtdChange({ from, to, endMonth }) {
			this.$emit("update-year-range", { from, to });
			this.$emit("update-end-month", endMonth);
		},
	},
};
</script>

<style scoped lang="scss">
.period_filters_row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.range_inline_calendar {
	// El calendar usa block layout adentro (.custom-dropdown wraps absolute).
	// Acá lo dejamos como inline-block para que el dropdown anclado se
	// posicione respecto al chip, no respecto al contenedor flex.
	display: inline-block;
}

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

// Los filtros consumen su acento propio (--report-filter-*) con fallback al
// primary/surface del tema (resuelto por elemento): Ventas/KPIs lo overridean
// con el teal vivo de marca; los demás tabs conservan su color de siempre.
.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-filter-wash, var(--report-surface));
	}

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover {
			background: var(--report-filter-accent-hover, var(--report-primary-hover));
		}
	}
}
</style>
